import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import type { ReactElement, ReactNode } from 'react';
import { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TextStyle } from '../../../../DesignSystem/Feedback/TextStyle';
import { IconButton } from '../../../../DesignSystem/Inputs/IconButton';

export type ConfirmationModalProps = {
  description: ReactNode;
  leftButton: ReactElement<HTMLButtonElement>;
  onClose: () => void;
  open: boolean;
  rightButton: ReactElement<HTMLButtonElement>;
  title: ReactNode;
};

const messages = defineMessages({
  close: {
    defaultMessage: 'Close',
    id: 'rbrahO',
  },
});

export function ConfirmationModal(props: ConfirmationModalProps) {
  const { description, leftButton, onClose, open, rightButton, title } = props;

  const { formatMessage } = useIntl();

  return (
    <Transition.Root as={Fragment} show={open}>
      <Dialog as="div" className="relative z-20" onClose={onClose} open={open}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-secondary bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative max-w-lg transform overflow-hidden rounded-md bg-gray-1 text-left shadow-xl-down transition-all sm:w-screen">
                <div className="relative w-full transform overflow-hidden rounded-lg bg-gray-1 p-6 text-left shadow-xl-down transition-all">
                  <header className="flex items-start justify-between">
                    <TextStyle
                      as="span"
                      className="text-gray-9"
                      variant="base-medium"
                    >
                      {title}
                    </TextStyle>
                    <IconButton
                      onClick={onClose}
                      variation="tertiaryLite"
                      size="xSmall"
                      className="-mr-1 -mt-1 ml-3"
                    >
                      <span className="sr-only">
                        {formatMessage(messages.close)}
                      </span>
                      <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </IconButton>
                  </header>
                  <TextStyle className="mt-2 text-gray-9" variant="sm-regular">
                    {description}
                  </TextStyle>
                  <div className="mt-6 flex gap-x-2">
                    {leftButton}
                    {rightButton}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
